import React, { useState, useRef, useEffect } from "react";
import "./App.css";
import Hero from "./components/Hero";
import Container from "./components/Container";
import Education from "./components/Education";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import Experience from "./components/Experience";
import Portfolio from "./components/Portfolio";
import BaxiTraining from "./components/BaxiTraining";
import Resources from "./components/Resources";
import RESOURCES, { thumbnailUrl } from "./data/resources";
import TopNav from "./components/TopNav";

/** Writings: title, URL, source (shown as a small label) */
const WRITINGS = [
  {
    source: "LinkedIn",
    title: "Foundational skills for early-career PMs",
    url: "https://www.linkedin.com/posts/daniel-sumah_foundational-skills-for-early-career-product-activity-7369669814388297728-jBF7",
  },
  {
    source: "LinkedIn",
    title: "Delegation series — how to choose the right person (chapter 2)",
    url: "https://www.linkedin.com/posts/daniel-sumah_chapter2delegationseriesbydanielsumah-activity-7239346903639883776-c6XJ",
  },
  {
    source: "LinkedIn",
    title: "Delegation series — what to delegate (chapter 1)",
    url: "https://www.linkedin.com/posts/daniel-sumah_chapter1delegationseriesbydanielsumah-activity-7235704136158097409-WKjc",
  },
  {
    source: "LinkedIn",
    title: "Delegation series — introduction",
    url: "https://www.linkedin.com/posts/daniel-sumah_introdelegationseriesbydanielsumah-activity-7234211993308528642-OZzS",
  },
  {
    source: "LinkedIn",
    title: "PM frameworks — MoSCoW",
    url: "https://www.linkedin.com/posts/daniel-sumah_pm-frameworks-moscow-by-daniel-sumah-activity-6835315717823307776-yJQ_",
  },
  {
    source: "LinkedIn",
    title: "PM frameworks — RICE",
    url: "https://www.linkedin.com/posts/daniel-sumah_rice-framework-by-daniel-sumah-activity-6831629246184595456-5JRA",
  },
  {
    source: "LinkedIn",
    title: "PM frameworks — value vs complexity",
    url: "https://www.linkedin.com/posts/daniel-sumah_pm-frameworks-value-complexity-framework-activity-6826452451244175360-avKz",
  },
  {
    source: "LinkedIn",
    title: "PM frameworks — the five whys",
    url: "https://www.linkedin.com/posts/daniel-sumah_the-five-whys-pm-frameworks-activity-6824331552218980353-esI_",
  },
  {
    source: "LinkedIn",
    title: "Quality function deployment — useful products",
    url: "https://www.linkedin.com/posts/daniel-sumah_quality-function-deployment-building-useful-activity-6817741902557786112-M-9q",
  },
  {
    source: "Medium",
    title: "Foundational skills for early-career product managers",
    url: "https://medium.com/@danielsumah/foundational-skills-for-early-career-product-managers-4980382fe935",
  },
  {
    source: "Medium",
    title: "Road to Opaline — this one is mine",
    url: "https://medium.com/@danielsumah/road-to-opaline-this-one-is-mine-9615249de2f6",
  },
  {
    source: "Medium",
    title: "A playbook for urgent product development",
    url: "https://medium.com/@danielsumah/a-playbook-for-urgent-product-development-574d465ccaf0",
  },
  {
    source: "Medium",
    title: "Day 2 of building X — Go and the schema",
    url: "https://medium.com/@danielsumah/day-2-of-building-x-a-look-at-go-and-the-schema-297b87fe331f",
  },
  {
    source: "Medium",
    title: "Day 1 of building X — an electricity monitoring device",
    url: "https://medium.com/@danielsumah/day-1-of-building-x-an-electricity-monitoring-device-1d4dc7448604",
  },
  {
    source: "Medium",
    title: "The joy of product management",
    url: "https://medium.com/@danielsumah/the-joy-of-product-management-96c45e1254e1",
  },
  {
    source: "Medium",
    title: "Solving designer–developer alignment in a small product team",
    url: "https://medium.com/@danielsumah/solving-designer-developer-alignment-problems-in-a-small-and-young-product-team-adfb4bcc3274",
  },
];

const SUBSTACK_URL = "https://danielsumah.substack.com";
const SUBSTACK_EMBED_URL = "https://danielsumah.substack.com/embed";
const BOOKS_READING = [
  // {
  //   label: "Book of the week",
  //   title: "The Queen's Code",
  //   url: "https://www.alisonarmstrong.com/products/queenscodebook.html",
  // },
  {
    label: "Book of the week",
    title: "Inspired by Marty Cagan",
    url: "https://www.svpg.com/books/inspired-how-to-create-tech-products-customers-love-2nd-edition/",
  },
];

function ScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (pathname !== "/" || !hash) return;
    const id = hash.slice(1);
    const attempt = (tries) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (tries > 0) {
        requestAnimationFrame(() => attempt(tries - 1));
      }
    };
    attempt(8);
  }, [pathname, hash]);
  return null;
}

function HomeResourceCard({ resource, styles, hoverTheme }) {
  const [imgOk, setImgOk] = useState(true);
  const [hovered, setHovered] = useState(false);

  return (
    <li style={styles.card}>
      <a
        href={resource.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          ...styles.cardLink,
          transform: hovered ? "translateY(-2px)" : "translateY(0)",
          boxShadow: hovered
            ? "0 12px 28px rgba(0,0,0,0.10)"
            : "0 2px 6px rgba(0,0,0,0.04)",
          borderColor: hovered ? hoverTheme.divider : "#d9d9d9",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div style={styles.thumbWrap}>
          {imgOk ? (
            <img
              src={thumbnailUrl(resource.slideId, 800)}
              alt={resource.title}
              style={styles.thumb}
              loading="lazy"
              onError={() => setImgOk(false)}
            />
          ) : (
            <div style={styles.thumbFallback}>{resource.source}</div>
          )}
        </div>
        <div style={styles.cardBody}>
          <p style={styles.kicker}>
            {resource.type} &middot; {resource.venue}
          </p>
          <h3 style={styles.cardTitle}>{resource.title}</h3>
        </div>
      </a>
    </li>
  );
}

function ScrollDownHint({ strokeColor, onClick }) {
  return (
    <button
      type="button"
      className="scroll-down-hint"
      aria-label="Scroll to next section"
      onClick={onClick}
    >
      <svg
        className="scroll-down-hint__chevron"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6 9l6 6 6-6"
          stroke={strokeColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function ReadingBanner() {
  const handleBookView = () => {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  };

  return (
    <aside className="reading-banner" aria-label="Books I'm reading">
      <div className="reading-banner__inner">
        <p className="reading-banner__heading">Books I'm reading</p>
        <div className="reading-banner__marquee">
          <div className="reading-banner__track">
            <span className="reading-banner__mobile-heading">
              Books I'm reading
            </span>
            {BOOKS_READING.map((book) => (
              <div className="reading-banner__book" key={book.url}>
                <span className="reading-banner__label">{book.label}</span>
                <span className="reading-banner__title">{book.title}</span>
                <a
                  className="reading-banner__button"
                  href={book.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleBookView}
                >
                  View
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}

function App() {
  const [isDarkMode] = useState(false);
  const [showOldSite, setShowOldSite] = useState(false);
  const [overDark, setOverDark] = useState(false);

  /* Dark / light toggle — commented out
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };
  */

  const toggleOldSite = () => {
    setShowOldSite(!showOldSite);
  };

  const theme = {
    light: {
      background: "#fafafa",
      text: "#333",
      textSecondary: "#555",
      textTertiary: "#666",
      textLight: "#888",
      nameColor: "#1a1a1a",
      divider: "#333",
      iconColor: "#333",
    },
    dark: {
      background: "#1a1a1a",
      text: "#e0e0e0",
      textSecondary: "#b0b0b0",
      textTertiary: "#999",
      textLight: "#777",
      nameColor: "#ffffff",
      divider: "#e0e0e0",
      iconColor: "#e0e0e0",
    }
  };

  const currentTheme = isDarkMode ? theme.dark : theme.light;
  /** Opposite palette: section 2 background/text invert relative to section 1 */
  const invertedTheme = isDarkMode ? theme.light : theme.dark;

  const introRef = useRef(null);
  const teachingRef = useRef(null);
  const speakingRef = useRef(null);
  const workRef = useRef(null);
  const resourcesRef = useRef(null);
  const writingsRef = useRef(null);
  const newsletterRef = useRef(null);
  const contactRef = useRef(null);
  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (!showOldSite) return;
    const html = document.documentElement;
    html.style.scrollSnapType = "none";
    return () => {
      html.style.scrollSnapType = "";
    };
  }, [showOldSite]);

  useEffect(() => {
    const darkSectionIds = new Set(["work", "speaking", "writings", "contact"]);
    const allIds = ["intro", "work", "teaching", "speaking", "resources", "writings", "newsletter", "contact"];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            setOverDark(darkSectionIds.has(entry.target.id));
          }
        });
      },
      { threshold: 0.5 }
    );
    allIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      flex: "1",
      width: "100%",
      backgroundColor: currentTheme.background,
      fontFamily: "'Georgia', serif",
      color: currentTheme.text,
      padding: "20px",
      transition: "background-color 0.3s ease, color 0.3s ease",
      position: "relative",
    },
    /* toggleButton / toggleIcon — used by dark mode switch (commented out)
    toggleButton: {
      position: "fixed",
      top: "30px",
      right: "30px",
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "10px",
      borderRadius: "50%",
      transition: "transform 0.3s ease",
      zIndex: 1001,
    },
    toggleIcon: {
      width: "28px",
      height: "28px",
    },
    */
    content: {
      textAlign: "center",
      maxWidth: "600px",
    },
    greeting: {
      fontSize: "0.9rem",
      fontWeight: "300",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: currentTheme.textLight,
      marginBottom: "0.5rem",
    },
    name: {
      fontSize: "3.5rem",
      fontWeight: "300",
      marginBottom: "2rem",
      letterSpacing: "0.05em",
      color: currentTheme.nameColor,
      transition: "color 0.3s ease",
    },
    divider: {
      width: "60px",
      height: "1px",
      backgroundColor: currentTheme.divider,
      margin: "2rem auto",
      transition: "background-color 0.3s ease",
    },
    interestsContainer: {
      marginBottom: "3rem",
    },
    intro: {
      fontSize: "1.1rem",
      lineHeight: "1.8",
      color: currentTheme.textSecondary,
      marginBottom: "1rem",
      fontWeight: "300",
      transition: "color 0.3s ease",
    },
    socialContainer: {
      display: "flex",
      gap: "2rem",
      marginTop: "3rem",
      justifyContent: "center",
    },
    socialLink: {
      fontSize: "1.5rem",
      color: currentTheme.iconColor,
      textDecoration: "none",
      transition: "color 0.3s ease",
      cursor: "pointer",
    },
    icon: {
      width: "32px",
      height: "32px",
      fill: currentTheme.iconColor,
      transition: "fill 0.3s ease",
    },
    workList: {
      listStyle: "none",
      padding: 0,
      margin: "0 0 0.5rem 0",
      width: "100%",
      maxWidth: "520px",
    },
    workItem: {
      marginBottom: "1.6rem",
      textAlign: "left",
    },
    workLink: {
      fontSize: "1.25rem",
      fontWeight: "300",
      letterSpacing: "0.02em",
      color: currentTheme.nameColor,
      textDecoration: "none",
      borderBottom: `1px solid ${currentTheme.divider}`,
      paddingBottom: "2px",
      transition: "color 0.2s ease, border-color 0.2s ease",
    },
    workBlurb: {
      fontSize: "0.98rem",
      lineHeight: "1.65",
      color: currentTheme.textSecondary,
      fontWeight: "300",
      margin: "0.45rem 0 0 0",
    },
    workSectionLead: {
      fontSize: "0.98rem",
      lineHeight: "1.65",
      color: currentTheme.textSecondary,
      fontWeight: "300",
      margin: "0 0 1.75rem 0",
      textAlign: "center",
      maxWidth: "520px",
    },
    workFootnote: {
      fontSize: "0.92rem",
      lineHeight: "1.6",
      color: currentTheme.textLight,
      fontWeight: "300",
      marginTop: "0.25rem",
      textAlign: "center",
      maxWidth: "520px",
    },
    oldSiteButton: {
      position: "fixed",
      bottom: "20px",
      left: "50%",
      transform: "translateX(-50%)",
      backgroundColor: "transparent",
      padding: "6px 14px",
      borderRadius: "15px",
      cursor: "pointer",
      fontSize: "0.75rem",
      fontFamily: "'Georgia', serif",
      transition: "opacity 0.3s ease, background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease",
      opacity: "0.3",
      zIndex: "1000",
    }
  };

  const stylesSection2 = {
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      flex: "1",
      width: "100%",
      backgroundColor: currentTheme.background,
      fontFamily: "'Georgia', serif",
      color: currentTheme.text,
      padding: "20px",
      transition: "background-color 0.3s ease, color 0.3s ease",
      position: "relative",
    },
    teachingHeading: {
      fontSize: "0.9rem",
      fontWeight: "300",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: currentTheme.textLight,
      marginBottom: "1rem",
    },
    teachingTitle: {
      fontSize: "2rem",
      fontWeight: "300",
      marginBottom: "1.5rem",
      letterSpacing: "0.03em",
      color: currentTheme.nameColor,
      textAlign: "center",
      maxWidth: "520px",
      lineHeight: "1.35",
    },
    teachingBody: {
      fontSize: "1.05rem",
      lineHeight: "1.8",
      color: currentTheme.textSecondary,
      fontWeight: "300",
      maxWidth: "520px",
      textAlign: "center",
      marginBottom: "2rem",
    },
    tribeCta: {
      display: "inline-block",
      padding: "12px 28px",
      borderRadius: "2px",
      border: `1px solid ${currentTheme.divider}`,
      color: currentTheme.nameColor,
      backgroundColor: "transparent",
      fontFamily: "'Georgia', serif",
      fontSize: "0.95rem",
      fontWeight: "400",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      textDecoration: "none",
      cursor: "pointer",
      transition: "background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease",
    },
  };

  const stylesWritings = {
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      flex: "1",
      width: "100%",
      backgroundColor: invertedTheme.background,
      fontFamily: "'Georgia', serif",
      color: invertedTheme.text,
      padding: "24px 20px 32px",
      transition: "background-color 0.3s ease, color 0.3s ease",
      position: "relative",
    },
    heading: {
      fontSize: "0.9rem",
      fontWeight: "300",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: invertedTheme.textLight,
      marginBottom: "0.5rem",
    },
    lead: {
      fontSize: "0.92rem",
      lineHeight: "1.5",
      color: invertedTheme.textSecondary,
      fontWeight: "300",
      margin: "0 0 1rem 0",
      textAlign: "center",
      maxWidth: "520px",
    },
    list: {
      listStyle: "none",
      padding: 0,
      margin: 0,
      width: "100%",
    },
    row: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "14px",
      padding: "11px 0",
      borderBottom: `1px solid ${invertedTheme.divider}`,
      textAlign: "left",
    },
    rowLast: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "14px",
      padding: "11px 0 0 0",
      textAlign: "left",
    },
    titleCol: {
      flex: 1,
      minWidth: 0,
    },
    titleText: {
      fontSize: "0.95rem",
      fontWeight: "400",
      lineHeight: "1.4",
      color: invertedTheme.nameColor,
      margin: 0,
    },
    sourceTag: {
      fontSize: "0.68rem",
      fontWeight: "600",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: invertedTheme.textLight,
      margin: "0.25rem 0 0 0",
      display: "block",
    },
    readBtn: {
      flexShrink: 0,
      display: "inline-block",
      padding: "7px 14px",
      borderRadius: "2px",
      border: `1px solid ${invertedTheme.divider}`,
      color: invertedTheme.nameColor,
      backgroundColor: "transparent",
      fontFamily: "'Georgia', serif",
      fontSize: "0.72rem",
      fontWeight: "500",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      textDecoration: "none",
      cursor: "pointer",
      transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",
      whiteSpace: "nowrap",
    },
  };

  const stylesNewsletter = {
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      flex: "1",
      width: "100%",
      backgroundColor: currentTheme.background,
      fontFamily: "'Georgia', serif",
      color: currentTheme.text,
      padding: "24px 20px 32px",
      transition: "background-color 0.3s ease, color 0.3s ease",
      position: "relative",
    },
    content: {
      width: "100%",
      maxWidth: "560px",
      textAlign: "center",
    },
    heading: {
      fontSize: "0.9rem",
      fontWeight: "300",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: currentTheme.textLight,
      marginBottom: "0.85rem",
    },
    title: {
      fontSize: "2.2rem",
      fontWeight: "300",
      lineHeight: "1.25",
      letterSpacing: "0.03em",
      color: currentTheme.nameColor,
      margin: "0 0 1rem 0",
    },
    lead: {
      fontSize: "1rem",
      lineHeight: "1.7",
      color: currentTheme.textSecondary,
      fontWeight: "300",
      margin: "0 auto 1.5rem auto",
      maxWidth: "500px",
    },
    embedWrap: {
      width: "100%",
      maxWidth: "480px",
      margin: "0 auto",
      border: 0,
      borderRadius: "4px",
      overflow: "hidden",
      backgroundColor: "#ffffff",
    },
    embed: {
      display: "block",
      width: "100%",
      height: "320px",
      border: "1px solid #EEE",
      backgroundColor: "#ffffff",
    },
    fallback: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      margin: "0 auto",
      minHeight: "320px",
    },
    fallbackButton: {
      display: "inline-block",
      padding: "12px 22px",
      borderRadius: "2px",
      border: `1px solid ${currentTheme.divider}`,
      color: currentTheme.nameColor,
      backgroundColor: "transparent",
      fontFamily: "'Georgia', serif",
      fontSize: "0.82rem",
      fontWeight: "500",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      cursor: "pointer",
      transition:
        "background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease",
      whiteSpace: "nowrap",
      textDecoration: "none",
    },
    note: {
      fontSize: "0.82rem",
      lineHeight: "1.6",
      color: currentTheme.textLight,
      fontWeight: "300",
      margin: "1rem auto 0 auto",
      maxWidth: "420px",
    },
  };

  const stylesResources = {
    lead: {
      fontSize: "0.95rem",
      lineHeight: "1.6",
      color: currentTheme.textSecondary,
      fontWeight: "300",
      margin: "0 0 2rem 0",
      textAlign: "center",
      maxWidth: "520px",
    },
    grid: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: "20px",
      listStyle: "none",
      padding: 0,
      margin: "0 0 2rem 0",
      width: "100%",
      maxWidth: "760px",
    },
    card: {
      flex: "1 1 280px",
      maxWidth: "340px",
      minWidth: 0,
    },
    cardLink: {
      display: "flex",
      flexDirection: "column",
      textDecoration: "none",
      color: "inherit",
      borderRadius: "4px",
      overflow: "hidden",
      border: `1px solid #d9d9d9`,
      backgroundColor: "#fff",
      transition:
        "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
      textAlign: "left",
      height: "100%",
    },
    thumbWrap: {
      position: "relative",
      width: "100%",
      paddingTop: "56.25%",
      backgroundColor: "#eceae6",
      overflow: "hidden",
    },
    thumb: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    },
    thumbFallback: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: currentTheme.textLight,
      fontSize: "0.72rem",
      letterSpacing: "0.12em",
      textTransform: "uppercase",
    },
    cardBody: {
      padding: "14px 16px 16px",
    },
    kicker: {
      fontSize: "0.68rem",
      fontWeight: "600",
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: currentTheme.textLight,
      margin: "0 0 6px 0",
    },
    cardTitle: {
      fontSize: "1rem",
      fontWeight: "400",
      color: currentTheme.nameColor,
      margin: 0,
      lineHeight: "1.35",
      letterSpacing: "0.01em",
    },
    viewAllCta: {
      display: "inline-block",
      padding: "12px 28px",
      borderRadius: "2px",
      border: `1px solid ${currentTheme.divider}`,
      color: currentTheme.nameColor,
      backgroundColor: "transparent",
      fontFamily: "'Georgia', serif",
      fontSize: "0.92rem",
      fontWeight: "400",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      textDecoration: "none",
      cursor: "pointer",
      transition:
        "background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease",
    },
  };

  const stylesSpeaking = {
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      flex: "1",
      width: "100%",
      backgroundColor: invertedTheme.background,
      fontFamily: "'Georgia', serif",
      color: invertedTheme.text,
      padding: "20px",
      transition: "background-color 0.3s ease, color 0.3s ease",
      position: "relative",
    },
    kicker: {
      fontSize: "0.9rem",
      fontWeight: "300",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: invertedTheme.textLight,
      marginBottom: "0.75rem",
      textAlign: "center",
    },
    title: {
      fontSize: "1.75rem",
      fontWeight: "300",
      letterSpacing: "0.03em",
      color: invertedTheme.nameColor,
      textAlign: "center",
      maxWidth: "520px",
      lineHeight: "1.35",
      margin: "0 auto 0.85rem auto",
    },
    lead: {
      fontSize: "0.97rem",
      lineHeight: "1.75",
      color: invertedTheme.textSecondary,
      fontWeight: "300",
      maxWidth: "520px",
      textAlign: "center",
      margin: "0 0 1.4rem 0",
    },
    submitBtn: {
      alignSelf: "flex-start",
      padding: "10px 24px",
      fontFamily: "'Georgia', serif",
      fontSize: "0.8rem",
      fontWeight: "400",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: invertedTheme.nameColor,
      backgroundColor: "transparent",
      border: `1px solid ${invertedTheme.divider}`,
      borderRadius: "2px",
      cursor: "pointer",
      transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",
    },
  };

  const stylesWorkDark = {
    container: {
      ...styles.container,
      backgroundColor: invertedTheme.background,
      color: invertedTheme.text,
    },
    greeting: {
      ...styles.greeting,
      color: invertedTheme.textLight,
    },
    workLink: {
      ...styles.workLink,
      color: invertedTheme.nameColor,
      borderBottom: `1px solid ${invertedTheme.divider}`,
    },
    workBlurb: {
      ...styles.workBlurb,
      color: invertedTheme.textSecondary,
    },
    workFootnote: {
      ...styles.workFootnote,
      color: invertedTheme.textLight,
    },
  };

  // If showing old site, render the old website with routing
  if (showOldSite) {
    return (
      <>
        <div className="App">
          <Router>
            <Routes>
              <Route path="/" element={<Hero />} />
              <Route path="/about" element={<Container />} />
              <Route path="/education" element={<Education />} />
              <Route path="/experience" element={<Experience />} />
              <Route path="/portfolio" element={<Portfolio />} />
            </Routes>
          </Router>
        </div>
        
        {/* Button to go back to new site */}
        <button 
          style={{
            ...styles.oldSiteButton,
            color: currentTheme.iconColor,
            border: `1px solid ${currentTheme.iconColor}`,
          }}
          onClick={toggleOldSite}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "0.8";
            e.currentTarget.style.backgroundColor = currentTheme.iconColor;
            e.currentTarget.style.color = currentTheme.background;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "0.3";
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = currentTheme.iconColor;
          }}
          title="Back to New Site"
        >
          ← New Site
        </button>
      </>
    );
  }

  // Otherwise, render the new simple site
  const homePage = (
    <>
      <div className="home-snap">
        {/* Dark mode toggle — commented out
        <button
          style={styles.toggleButton}
          onClick={toggleDarkMode}
          onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
          onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
          title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          {isDarkMode ? (
            <svg style={{ ...styles.toggleIcon, fill: fixedUiTheme.iconColor }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z"/>
            </svg>
          ) : (
            <svg style={{ ...styles.toggleIcon, fill: fixedUiTheme.iconColor }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M9.37,5.51C9.19,6.15,9.1,6.82,9.1,7.5c0,4.08,3.32,7.4,7.4,7.4c0.68,0,1.35-0.09,1.99-0.27C17.45,17.19,14.93,19,12,19 c-3.86,0-7-3.14-7-7C5,9.07,6.81,6.55,9.37,5.51z M12,3c-4.97,0-9,4.03-9,9s4.03,9,9,9s9-4.03,9-9c0-0.46-0.04-0.92-0.1-1.36 c-0.98,1.37-2.58,2.26-4.4,2.26c-2.98,0-5.4-2.42-5.4-5.4c0-1.81,0.89-3.42,2.26-4.4C12.92,3.04,12.46,3,12,3L12,3z"/>
            </svg>
          )}
        </button>
        */}

        <section
          id="intro"
          className="home-snap-section"
          aria-label="Introduction"
          ref={introRef}
        >
          <div style={styles.container}>
            <div style={styles.content}>
              <p style={styles.greeting}>My name is</p>
              <h1 style={styles.name}>Daniel Sumah</h1>
              
              <div style={styles.divider}></div>
              
              <div style={styles.interestsContainer}>
                <p style={styles.intro}>
                  I'm passionate about systems thinking, building products that matter, and mentoring.
                </p>
                <p style={styles.intro}>
                  I enjoy playing the piano, swimming, and having meaningful conversations.
                </p>
              </div>

            </div>
          </div>
          <ScrollDownHint
            strokeColor={currentTheme.iconColor}
            onClick={() => scrollToSection(workRef)}
          />
        </section>

        <section
          id="work"
          className="home-snap-section"
          aria-label="Work and projects"
          ref={workRef}
        >
          <div style={stylesWorkDark.container}>
            <p style={stylesWorkDark.greeting}>Work &amp; products</p>

            <ul style={styles.workList}>
              <li style={styles.workItem}>
                <a
                  href="https://www.shuttlers.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={stylesWorkDark.workLink}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = invertedTheme.textSecondary;
                    e.currentTarget.style.borderColor = invertedTheme.textSecondary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = invertedTheme.nameColor;
                    e.currentTarget.style.borderColor = invertedTheme.divider;
                  }}
                >
                  Shuttlers
                </a>
                <p style={stylesWorkDark.workBlurb}>
                  Where I work — Senior Product Lead, building products for scheduled shared transportation.
                </p>
              </li>
              <li style={styles.workItem}>
                <a
                  href="https://www.workbucket.xyz"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={stylesWorkDark.workLink}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = invertedTheme.textSecondary;
                    e.currentTarget.style.borderColor = invertedTheme.textSecondary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = invertedTheme.nameColor;
                    e.currentTarget.style.borderColor = invertedTheme.divider;
                  }}
                >
                  Workbucket
                </a>
                <p style={stylesWorkDark.workBlurb}>
                  An app for teams to work the way they want.
                </p>
              </li>
              <li style={styles.workItem}>
                <a
                  href="https://expo.dev/accounts/sumah/projects/repstats/builds/68b1b264-76f0-417e-a087-8c497624fe64"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={stylesWorkDark.workLink}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = invertedTheme.textSecondary;
                    e.currentTarget.style.borderColor = invertedTheme.textSecondary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = invertedTheme.nameColor;
                    e.currentTarget.style.borderColor = invertedTheme.divider;
                  }}
                >
                  Repstats
                </a>
                <p style={stylesWorkDark.workBlurb}>
                  Personal side project — an exercise tracking app; Android build via Expo.
                </p>
              </li>
            </ul>
            <p style={stylesWorkDark.workFootnote}>
              There&apos;s more. Wanna know? happy to chat.
            </p>
          </div>
          <ScrollDownHint
            strokeColor={invertedTheme.iconColor}
            onClick={() => scrollToSection(teachingRef)}
          />
        </section>

        <section
          id="teaching"
          className="home-snap-section"
          aria-label="Teaching and mentoring"
          ref={teachingRef}
        >
          <div style={stylesSection2.container}>
            <p style={stylesSection2.teachingHeading}>Teaching &amp; mentoring</p>
            <h2 style={stylesSection2.teachingTitle}>Product management with Tribe of Product</h2>
            <p style={stylesSection2.teachingBody}>
              Tribe of Product is how I help PMs grow — through 1:1 sessions,
              mentorship, and paid training programs.
            </p>
            <a
              href="https://tribeofproduct.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={stylesSection2.tribeCta}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = currentTheme.iconColor;
                e.currentTarget.style.color = currentTheme.background;
                e.currentTarget.style.borderColor = currentTheme.iconColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = currentTheme.nameColor;
                e.currentTarget.style.borderColor = currentTheme.divider;
              }}
            >
              Visit tribeofproduct.com
            </a>
          </div>
          <ScrollDownHint
            strokeColor={currentTheme.iconColor}
            onClick={() => scrollToSection(speakingRef)}
          />
        </section>

        <section
          id="speaking"
          className="home-snap-section"
          aria-label="Speaking and Events"
          ref={speakingRef}
        >
          <div style={stylesSpeaking.container}>
            <div className="speaking-scroll">
              <p style={stylesSpeaking.kicker}>Speaking &amp; Events</p>
              <h2 style={stylesSpeaking.title}>Available to speak at schools, events &amp; conferences</h2>
              <p style={stylesSpeaking.lead}>
                I enjoy sharing practical lessons from building products, leading initiatives, and creating technology solutions for students, businesses and communities.
              </p>

              <a
                href="https://forms.gle/ToCH9irPhhpN6rYEA"
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...stylesSpeaking.submitBtn, alignSelf: "center" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = invertedTheme.iconColor;
                  e.currentTarget.style.color = invertedTheme.background;
                  e.currentTarget.style.borderColor = invertedTheme.iconColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = invertedTheme.nameColor;
                  e.currentTarget.style.borderColor = invertedTheme.divider;
                }}
              >
                Request a Speaking Engagement
              </a>
            </div>
          </div>
          <ScrollDownHint
            strokeColor={invertedTheme.iconColor}
            onClick={() => scrollToSection(resourcesRef)}
          />
        </section>

        <section
          id="resources"
          className="home-snap-section"
          aria-label="Resources"
          ref={resourcesRef}
        >
          <div style={styles.container}>
            <p style={styles.greeting}>Resources</p>
            <p style={stylesResources.lead}>
              A growing collection of decks. Click a card to open the full
              deck.
            </p>
            <ul style={stylesResources.grid}>
              {RESOURCES.slice(0, 2).map((r) => (
                <HomeResourceCard
                  key={r.id}
                  resource={r}
                  styles={stylesResources}
                  hoverTheme={currentTheme}
                />
              ))}
            </ul>
            <Link
              to="/resources"
              style={stylesResources.viewAllCta}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = currentTheme.iconColor;
                e.currentTarget.style.color = currentTheme.background;
                e.currentTarget.style.borderColor = currentTheme.iconColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = currentTheme.nameColor;
                e.currentTarget.style.borderColor = currentTheme.divider;
              }}
            >
              View all resources
            </Link>
          </div>
          <ScrollDownHint
            strokeColor={currentTheme.iconColor}
            onClick={() => scrollToSection(writingsRef)}
          />
        </section>

        <section
          id="writings"
          className="home-snap-section"
          aria-label="Writings"
          ref={writingsRef}
        >
          <div style={stylesWritings.container}>
            <p style={stylesWritings.heading}>Writings</p>
            <p style={stylesWritings.lead}>
              Pick a piece to open in a new tab.
            </p>
            <div className="writings-scroll">
              <ul style={stylesWritings.list}>
                {WRITINGS.map((w, i) => (
                  <li
                    key={w.url}
                    style={
                      i === WRITINGS.length - 1
                        ? stylesWritings.rowLast
                        : stylesWritings.row
                    }
                  >
                    <div style={stylesWritings.titleCol}>
                      <p style={stylesWritings.titleText}>{w.title}</p>
                      <span style={stylesWritings.sourceTag}>{w.source}</span>
                    </div>
                    <a
                      href={w.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={stylesWritings.readBtn}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          invertedTheme.iconColor;
                        e.currentTarget.style.color = invertedTheme.background;
                        e.currentTarget.style.borderColor =
                          invertedTheme.iconColor;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = invertedTheme.nameColor;
                        e.currentTarget.style.borderColor = invertedTheme.divider;
                      }}
                    >
                      Read
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <ScrollDownHint
            strokeColor={invertedTheme.iconColor}
            onClick={() => scrollToSection(newsletterRef)}
          />
        </section>

        <section
          id="newsletter"
          className="home-snap-section"
          aria-label="Newsletter"
          ref={newsletterRef}
        >
          <div style={stylesNewsletter.container}>
            <div style={stylesNewsletter.content}>
              <p style={stylesNewsletter.heading}>Newsletter</p>
              <h2 style={stylesNewsletter.title}>Notes on products, people, and systems</h2>
              <p style={stylesNewsletter.lead}>
                Lessons from building, teaching, and leading product teams,
                sent straight to your inbox.
              </p>

              <div style={stylesNewsletter.embedWrap}>
                <iframe
                  title="Subscribe to Daniel Sumah on Substack"
                  src={SUBSTACK_EMBED_URL}
                  width="480"
                  height="320"
                  style={stylesNewsletter.embed}
                  frameBorder="0"
                  scrolling="no"
                >
                  <div style={stylesNewsletter.fallback}>
                    <a
                      href={SUBSTACK_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={stylesNewsletter.fallbackButton}
                    >
                      Subscribe on Substack
                    </a>
                  </div>
                </iframe>
              </div>

              <p style={stylesNewsletter.note}>
                Subscribe here through Substack&apos;s embedded form.
              </p>
            </div>
          </div>
          <ScrollDownHint
            strokeColor={currentTheme.iconColor}
            onClick={() => scrollToSection(contactRef)}
          />
        </section>

        <section
          id="contact"
          className="home-snap-section"
          aria-label="Contact"
          ref={contactRef}
        >
          <div style={{ ...styles.container, backgroundColor: invertedTheme.background, color: invertedTheme.text }}>
            <h2
              style={{
                fontSize: "2rem",
                fontWeight: "300",
                letterSpacing: "0.03em",
                color: invertedTheme.nameColor,
                marginBottom: "2rem",
                textAlign: "center",
              }}
            >
              How to contact me
            </h2>
            <div style={{ ...styles.socialContainer, marginTop: 0 }}>
              <a
                href="https://www.linkedin.com/in/daniel-sumah/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...styles.socialLink, color: invertedTheme.iconColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#0077b5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = invertedTheme.iconColor;
                }}
                title="LinkedIn"
              >
                <svg style={{ ...styles.icon, fill: invertedTheme.iconColor }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>

              <a
                href="https://danielsumah.medium.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...styles.socialLink, color: invertedTheme.iconColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#00ab6c";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = invertedTheme.iconColor;
                }}
                title="Medium"
              >
                <svg style={{ ...styles.icon, fill: invertedTheme.iconColor }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z"/>
                </svg>
              </a>

              <a
                href="https://substack.com/@danielsumah"
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...styles.socialLink, color: invertedTheme.iconColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#ff6719";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = invertedTheme.iconColor;
                }}
                title="Substack"
              >
                <svg style={{ ...styles.icon, fill: invertedTheme.iconColor }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/>
                </svg>
              </a>

              <a
                href="mailto:danielsumah.office@gmail.com"
                style={{ ...styles.socialLink, color: invertedTheme.iconColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#ea4335";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = invertedTheme.iconColor;
                }}
                title="Email"
              >
                <svg style={{ ...styles.icon, fill: invertedTheme.iconColor }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </a>

              <a
                href="tel:+2348147061806"
                style={{ ...styles.socialLink, color: invertedTheme.iconColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#34a853";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = invertedTheme.iconColor;
                }}
                title="Call Office"
              >
                <svg style={{ ...styles.icon, fill: invertedTheme.iconColor }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.47 11.47 0 003.58.57 1 1 0 011 1V21a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.5a1 1 0 011 1 11.47 11.47 0 00.57 3.58 1 1 0 01-.25 1.01l-2.2 2.2z"/>
                </svg>
              </a>
            </div>
          </div>
        </section>
      </div>

      {/* Button to view old site — hidden for now
      <button 
        style={{
          ...styles.oldSiteButton,
          color: fixedUiTheme.iconColor,
          border: `1px solid ${fixedUiTheme.iconColor}`,
        }}
        onClick={toggleOldSite}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = "0.8";
          e.currentTarget.style.backgroundColor = fixedUiTheme.iconColor;
          e.currentTarget.style.color = fixedUiTheme.background;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = "0.3";
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.color = fixedUiTheme.iconColor;
        }}
        title="View Old Portfolio Website"
      >
        Old Site →
      </button>
      */}
    </>
  );

  return (
    <Router>
      <TopNav />
      <ReadingBanner />
      <ScrollToHash />
      <Routes>
        <Route path="/" element={homePage} />
        <Route path="/baxi-training" element={<BaxiTraining />} />
        <Route path="/resources" element={<Resources />} />
      </Routes>
      <a
        href="tel:+2348147061806"
        title="Call Daniel Sumah's Office"
        style={{
          position: "fixed",
          bottom: "28px",
          right: "28px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "12px 20px",
          backgroundColor: overDark ? "#fafafa" : "#1a1a1a",
          color: overDark ? "#1a1a1a" : "#fafafa",
          borderRadius: "50px",
          textDecoration: "none",
          fontFamily: "'Georgia', serif",
          fontSize: "0.82rem",
          fontWeight: "400",
          letterSpacing: "0.06em",
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          zIndex: 2000,
          transition: "background-color 0.3s ease, color 0.3s ease, box-shadow 0.2s ease, transform 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = overDark ? "#e0e0e0" : "#333";
          e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.22)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = overDark ? "#fafafa" : "#1a1a1a";
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.18)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill={overDark ? "#1a1a1a" : "#fafafa"}
          xmlns="http://www.w3.org/2000/svg"
          style={{ flexShrink: 0 }}
        >
          <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.47 11.47 0 003.58.57 1 1 0 011 1V21a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.5a1 1 0 011 1 11.47 11.47 0 00.57 3.58 1 1 0 01-.25 1.01l-2.2 2.2z"/>
        </svg>
        Reach Out
      </a>
    </Router>
  );
}

export default App;
